import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Clock, IndianRupee, GraduationCap, Eye, Star, CheckCircle, 
  BookOpen, ClipboardCheck, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * CourseCardWithMode - Course card with STT/RPL mode selection
 * 
 * Props:
 * - course: { id, title, description, fee, duration, image, badges, popular }
 * - onEnroll: (course, mode, fee) => void
 * - onViewDetails: (course) => void
 * - featured: boolean
 * - showImage: boolean (default true)
 * - compact: boolean (for dashboard usage)
 */
const CourseCardWithMode = ({ 
  course, 
  onEnroll, 
  onViewDetails,
  featured = false,
  showImage = true,
  compact = false
}) => {
  const [selectedMode, setSelectedMode] = useState(null);
  const [showModeError, setShowModeError] = useState(false);
  
  // Calculate RPL fee (20% of STT/original fee)
  const sttFee = course.fee || course.course_fee || 0;
  const rplFee = Math.round(sttFee * 0.2);
  
  const handleEnrollClick = () => {
    if (!selectedMode) {
      setShowModeError(true);
      toast.error('Please select STT or RPL mode before enrolling');
      return;
    }
    
    const finalFee = selectedMode === 'STT' ? sttFee : rplFee;
    onEnroll(course, selectedMode, finalFee);
  };
  
  const handleModeSelect = (mode) => {
    setSelectedMode(mode);
    setShowModeError(false);
  };
  
  // Mode info based on selection
  const getModeInfo = () => {
    if (selectedMode === 'STT') {
      return {
        label: 'Full Training Included',
        duration: course.duration || '3 Months',
        description: 'Complete training with certification'
      };
    }
    if (selectedMode === 'RPL') {
      return {
        label: 'Assessment Based',
        duration: 'Direct Exam',
        description: 'No training - Direct certification exam'
      };
    }
    return null;
  };
  
  const modeInfo = getModeInfo();

  if (compact) {
    // Compact version for dashboards
    return (
      <div className={`bg-white rounded-xl border border-slate-200 p-4 transition-all hover:shadow-lg ${featured ? 'ring-2 ring-blue-500' : ''}`}>
        <div className="flex items-start gap-4">
          {showImage && course.image && (
            <img 
              src={course.image} 
              alt={course.title || course.name}
              className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-slate-900 truncate">{course.title || course.name}</h4>
            <p className="text-sm text-slate-500 mb-2">{course.duration || '3 Months'}</p>
            
            {/* STT/RPL Mode Selection - Compact */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-slate-600">Select Mode:</p>
              <div className="flex gap-2">
                <button
                  onClick={() => handleModeSelect('STT')}
                  className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all border-2 ${
                    selectedMode === 'STT'
                      ? 'bg-blue-50 border-blue-500 text-blue-700'
                      : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-center gap-1">
                    <BookOpen className="w-3 h-3" />
                    <span>STT</span>
                  </div>
                  <div className="font-bold mt-0.5">₹{sttFee.toLocaleString()}</div>
                </button>
                <button
                  onClick={() => handleModeSelect('RPL')}
                  className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all border-2 ${
                    selectedMode === 'RPL'
                      ? 'bg-purple-50 border-purple-500 text-purple-700'
                      : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-center gap-1">
                    <ClipboardCheck className="w-3 h-3" />
                    <span>RPL</span>
                  </div>
                  <div className="font-bold mt-0.5">₹{rplFee.toLocaleString()}</div>
                </button>
              </div>
              
              {showModeError && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Please select STT or RPL mode
                </p>
              )}
              
              {modeInfo && (
                <p className="text-xs text-slate-500">{modeInfo.label}</p>
              )}
            </div>
          </div>
        </div>
        
        <div className="mt-3 flex gap-2">
          <Button 
            size="sm"
            onClick={handleEnrollClick}
            className="flex-1 bg-blue-600 hover:bg-blue-700"
          >
            Enroll Now
          </Button>
          {onViewDetails && (
            <Button 
              size="sm"
              variant="outline"
              onClick={() => onViewDetails(course)}
            >
              <Eye className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Full version for public course page
  return (
    <div 
      className={`group bg-white rounded-2xl border border-slate-200 overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${featured ? 'ring-2 ring-blue-500' : ''}`}
      data-testid={`course-card-${course.id}`}
    >
      {/* Image */}
      {showImage && (
        <div className="relative h-48 overflow-hidden">
          <img 
            src={course.image || '/api/placeholder/400/250'} 
            alt={course.title || course.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          {course.popular && (
            <span className="absolute top-3 left-3 bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
              <Star className="w-3 h-3" /> Most Popular
            </span>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          {course.badges && (
            <div className="absolute bottom-3 left-3 right-3 flex gap-2">
              {course.badges.slice(0, 2).map((badge, i) => (
                <span key={i} className="bg-white/90 text-slate-700 px-2 py-1 rounded text-xs font-medium flex items-center gap-1">
                  <CheckCircle className="w-3 h-3 text-emerald-500" /> {badge}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <div className="p-5">
        <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">
          {course.title || course.name}
        </h3>
        <p className="text-slate-600 text-sm mb-4 line-clamp-2">{course.description}</p>

        {/* Duration */}
        <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" /> {course.duration || '3 Months'}
          </span>
        </div>

        {/* STT/RPL Mode Selection */}
        <div className="mb-4 p-3 bg-slate-50 rounded-xl">
          <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <GraduationCap className="w-4 h-4 text-blue-600" />
            Select Training Mode:
          </p>
          
          <div className="grid grid-cols-2 gap-3">
            {/* STT Option */}
            <button
              onClick={() => handleModeSelect('STT')}
              className={`relative p-3 rounded-xl text-left transition-all border-2 ${
                selectedMode === 'STT'
                  ? 'bg-blue-50 border-blue-500 shadow-sm'
                  : 'bg-white border-slate-200 hover:border-blue-300'
              }`}
            >
              {selectedMode === 'STT' && (
                <div className="absolute -top-2 -right-2 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-3 h-3 text-white" />
                </div>
              )}
              <div className="flex items-center gap-2 mb-1">
                <BookOpen className={`w-4 h-4 ${selectedMode === 'STT' ? 'text-blue-600' : 'text-slate-500'}`} />
                <span className={`font-bold ${selectedMode === 'STT' ? 'text-blue-700' : 'text-slate-700'}`}>
                  STT
                </span>
              </div>
              <div className={`text-lg font-bold mb-1 ${selectedMode === 'STT' ? 'text-blue-700' : 'text-slate-800'}`}>
                ₹{sttFee.toLocaleString()}
              </div>
              <p className="text-xs text-slate-500">Training Included</p>
            </button>
            
            {/* RPL Option */}
            <button
              onClick={() => handleModeSelect('RPL')}
              className={`relative p-3 rounded-xl text-left transition-all border-2 ${
                selectedMode === 'RPL'
                  ? 'bg-purple-50 border-purple-500 shadow-sm'
                  : 'bg-white border-slate-200 hover:border-purple-300'
              }`}
            >
              {selectedMode === 'RPL' && (
                <div className="absolute -top-2 -right-2 w-5 h-5 bg-purple-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-3 h-3 text-white" />
                </div>
              )}
              <div className="flex items-center gap-2 mb-1">
                <ClipboardCheck className={`w-4 h-4 ${selectedMode === 'RPL' ? 'text-purple-600' : 'text-slate-500'}`} />
                <span className={`font-bold ${selectedMode === 'RPL' ? 'text-purple-700' : 'text-slate-700'}`}>
                  RPL
                </span>
              </div>
              <div className={`text-lg font-bold mb-1 ${selectedMode === 'RPL' ? 'text-purple-700' : 'text-slate-800'}`}>
                ₹{rplFee.toLocaleString()}
              </div>
              <p className="text-xs text-slate-500">Assessment Only</p>
            </button>
          </div>
          
          {/* Mode Error */}
          {showModeError && (
            <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              Please select STT or RPL before enrolling
            </div>
          )}
          
          {/* Selected Mode Info */}
          {modeInfo && (
            <div className={`mt-3 p-2 rounded-lg text-sm ${
              selectedMode === 'STT' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
            }`}>
              <div className="flex items-center justify-between">
                <span className="font-medium">{modeInfo.label}</span>
                <span>{modeInfo.duration}</span>
              </div>
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="flex gap-2">
          <Button 
            onClick={handleEnrollClick}
            className={`flex-1 ${
              selectedMode 
                ? selectedMode === 'STT' 
                  ? 'bg-blue-600 hover:bg-blue-700' 
                  : 'bg-purple-600 hover:bg-purple-700'
                : 'bg-slate-400 hover:bg-slate-500'
            }`}
          >
            <GraduationCap className="w-4 h-4 mr-1" /> 
            {selectedMode ? `Enroll (${selectedMode})` : 'Enroll Now'}
          </Button>
          {onViewDetails && (
            <Button 
              variant="outline"
              className="border-slate-300 hover:bg-slate-50"
              onClick={() => onViewDetails(course)}
            >
              <Eye className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseCardWithMode;
